import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";

const NursingRecordList = ({ records = [], loading, onAdd, onEdit, onDelete }) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Filtering
    const filteredRecords = records.filter((r) =>
        Object.values(r).some((v) =>
            String(v).toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredRecords.length;
    const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    // Delete confirmation
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Confirm Delete",
            text: "Do you want to delete this Nursing Record?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                if (onDelete) {
                    await onDelete(id); // await the parent deletion function
                    Swal.fire("Deleted!", "Nursing Record has been removed.", "success");
                } else {
                    Swal.fire("Error!", "Delete function not defined.", "error");
                }
            } catch (err) {
                Swal.fire("Error!", "Failed to delete record.", "error");
            }
        }
    };

    // PDF Export
    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Nursing Records", 14, 10);
        const tableColumn = [
            "#",
            "Name",
            "M.R No",
            "Room No",
            "Consultant",
            "Remarks",
            "Nurse",
            "Created By",
            "Updated By",
        ];
        const tableRows = filteredRecords.map((r, i) => [
            i + 1,
            r.name,
            r.mr_no,
            r.room_no,
            r.consultant,
            r.remarks,
            r.nurse,
            r.created_by,
            r.updated_by,
        ]);
        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
        doc.save("nursing_records.pdf");
    };

    // Custom Pagination
    const CustomPagination = ({
        onChangePage,
        onChangeRowsPerPage,
        currentPage,
        rowsPerPage,
        rowCount,
    }) => {
        const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                        Rows per page:
                    </span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={(e) =>
                            onChangeRowsPerPage(Number(e.target.value), currentPage)
                        }
                    >
                        {[5, 10, 15, 20].map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(Math.max(1, currentPage - 1), rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(Math.min(totalPages, currentPage + 1), rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    // Table Columns
    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "70px",
        },
        { name: "Name", selector: (r) => r.name, sortable: true },
        { name: "M.R No", selector: (r) => r.mr_no, sortable: true },
        { name: "Room No", selector: (r) => r.room_no },
        { name: "Consultant", selector: (r) => r.consultant },
        { name: "Remarks", selector: (r) => r.remarks },
        { name: "Nurse", selector: (r) => r.nurse },
        { name: "Created By", selector: (r) => r.created_by },
        { name: "Updated By", selector: (r) => r.updated_by },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEdit && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit"
                            onClick={() => onEdit(row.id)}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDelete && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                            onClick={() => handleDelete(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            width: "130px",
            ignoreRowClick: true,
            button: true,
        },
    ];

    return (
        <div>
            {/* Header */}
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                {/* Left: Export + Search */}
                <div className="d-flex align-items-center gap-2 position-relative">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button className="dropdown-item" onClick={handleExportPDF}>
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={filteredRecords}
                                    filename="nursing_records.csv"
                                    className="dropdown-item"
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search Nursing Records..."
                        className="form-control form-control-sm"
                        style={{ width: "260px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </div>

                {/* Center Info */}
                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    Showing {start}-{end} of {totalRows} records
                </h6>

                {/* Right: Add button */}
                {onAdd && (
                    <div>
                        <button className="btn btn-sm btn-primary" onClick={onAdd}>
                            <i className="ri-add-line me-1"></i> Add New
                        </button>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredRecords}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    progressPending={loading}
                    noDataComponent={<div className="p-3 text-muted">No records found</div>}
                />
            </div>
        </div>
    );
};

export default NursingRecordList;
